import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { notify } from "./toast";

/**
 * Runs one of the panel's actions: it reports how it went and refreshes what is on screen.
 *
 * Every write goes through here, so the panel has one notion of "busy" — the buttons that
 * would send something disable themselves while anything is in flight — and an action that
 * fails always says why instead of leaving the screen pretending it worked.
 *
 * Resolves `true` when the action succeeded.
 */
export function useRun() {
  const queryClient = useQueryClient();
  const busy = useIsMutating() > 0;
  const { mutateAsync } = useMutation({ mutationFn: (action: () => Promise<unknown>) => action() });

  const run = useCallback(
    async (action: () => Promise<unknown>, successMessage?: string): Promise<boolean> => {
      try {
        await mutateAsync(action);
      } catch (error) {
        notify(error instanceof Error && error.message ? error.message : "No se pudo completar la acción", true);
        return false;
      }
      if (successMessage) notify(successMessage);
      await queryClient.invalidateQueries();
      return true;
    },
    [mutateAsync, queryClient],
  );

  return { run, busy };
}

/** Whether any action is in flight, for the buttons that only need to know that. */
export function useBusy(): boolean {
  return useIsMutating() > 0;
}
